import { createLogger } from '../utils/logger';
import { supabase } from '../utils/supabase';
import { initializeNotificationsV2 } from './notificationsV2Integration';
import { processEvents } from './EventProcessorService';
// WebSocket manager removed - using polling instead

const logger = createLogger({ module: 'AppInitialization', level: 'warn' });

interface InitializationOptions {
  enableNotifications?: boolean;
  enableEventProcessing?: boolean;
  debugMode?: boolean;
}

class AppInitializationService {
  private static instance: AppInitializationService;
  private isInitialized = false;
  private eventProcessorService: any = null;
  private eventProcessingInterval: ReturnType<typeof setTimeout> | null = null;
  private eventProcessingSubscriptionId: string | null = null;

  static getInstance(): AppInitializationService {
    if (!AppInitializationService.instance) {
      AppInitializationService.instance = new AppInitializationService();
    }
    return AppInitializationService.instance;
  }

  async initialize(options: InitializationOptions = {}): Promise<void> {
    if (this.isInitialized) {
      logger.debug('App already initialized');
      return;
    }

    const {
      enableNotifications = true,
      enableEventProcessing = true,
      debugMode = import.meta.env.MODE === 'development'
    } = options;

    try {
      logger.debug('Starting application initialization...');

      // Initialize NotificationsV2 system
      if (enableNotifications) {
        await this.initializeNotificationSystem(enableEventProcessing, debugMode);
      }

      // Start event processing
      await this.startEventProcessing();

      // Mark as initialized
      this.isInitialized = true;
      logger.info('Application initialization completed successfully');

    } catch (error) {
      logger.error('Application initialization failed:', error);
      throw error;
    }
  }

  private async initializeNotificationSystem(enableEventProcessing: boolean, debugMode: boolean): Promise<void> {
    try {
      logger.info('Initializing NotificationsV2 system...');

      // Check if notification templates exist
      const { data: templates, error: templatesError } = await supabase
        .from('notification_templates')
        .select('count')
        .limit(1);

      if (templatesError) {
        logger.warn('Could not check notification templates:', templatesError);
      } else if (!templates || templates.length === 0) {
        logger.warn('No notification templates found. Please run the template creation script.');
      }

      // Initialize event processor if enabled
      if (enableEventProcessing) {
        await this.initializeEventProcessor(debugMode);
      }

      // Disabled - NotificationContext handles WebSocket subscriptions
      // await this.setupEventProcessingSubscription();

      logger.info('NotificationsV2 system initialized successfully');

    } catch (error) {
      logger.error('Failed to initialize NotificationsV2 system:', error);
      throw error;
    }
  }

  private async initializeEventProcessor(debugMode: boolean): Promise<void> {
    try {
      // Use the already imported processEvents function instead of dynamic import
      this.eventProcessorService = { processUnprocessedEvents: processEvents };
      
      if (debugMode) {
        logger.debug('Event processor initialized in debug mode');
      }

    } catch (error) {
      logger.error('Failed to initialize event processor:', error);
      throw error;
    }
  }

  private async setupEventProcessingSubscription(): Promise<void> {
    try {
      // Check if user is authenticated before creating subscription
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        logger.debug('No authenticated session, skipping event processing subscription');
        return;
      }

      // WebSocket subscriptions removed - event processing handled by polling
      // The polling notification service handles all notification delivery
      /*
      const subscriptionId = await wsManager.subscribe({
        name: 'notifications_realtime',
        type: 'postgres_changes',
        filter: {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        callback: async (payload) => {
          try {
            logger.debug('New notification received:', payload.new);
            
            // The notification has already been created by the database processor
            // This is just for real-time updates to the UI
            // The actual notification will be handled by NotificationContext
          } catch (error) {
            logger.error('Error handling notification:', error);
          }
        },
        onError: (error) => {
          logger.error('Notification subscription error:', error);
        },
        priority: 'high',
        retryable: true
      });
      */
      
      // Event processing now handled by polling notification service
      logger.info('Event processing handled by polling notification service');

    } catch (error) {
      logger.error('Failed to setup event processing subscription:', error);
      // Don't throw - allow the app to continue without realtime
    }
  }

  private async startEventProcessing(): Promise<void> {
    try {
      // Check if user is authenticated before starting event processing
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        logger.debug('No authenticated session, skipping event processing startup');
        return;
      }

      logger.info('Starting event processing...');

      // Clear any existing interval to prevent duplicates
      if (this.eventProcessingInterval) {
        clearInterval(this.eventProcessingInterval);
        this.eventProcessingInterval = null;
      }

      // Process any existing unprocessed events immediately
      await processEvents();

      // Set up periodic event processing (every 30 seconds)
      this.eventProcessingInterval = setInterval(async () => {
        try {
          // Check auth state before each processing run
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            logger.debug('No session during periodic event processing, skipping');
            return;
          }
          await processEvents();
        } catch (error) {
          logger.error('Error in periodic event processing:', error);
        }
      }, 30000); // 30 seconds

      logger.info('Event processing started successfully');
    } catch (error) {
      logger.error('Failed to start event processing:', error);
      throw error;
    }
  }

  async shutdown(): Promise<void> {
    try {
      logger.info('Shutting down application services...');

      // Remove event processing subscription if it exists
      if (this.eventProcessingSubscriptionId) {
        try {
          // WebSocket unsubscribe removed
        } catch (error) {
          logger.warn('Error removing event processing subscription:', error);
        }
        this.eventProcessingSubscriptionId = null;
      }

      // Clean up all WebSocket connections via manager
      try {
        // WebSocket cleanup removed
      } catch (error) {
        logger.warn('Error cleaning up WebSocket manager:', error);
      }

      // Stop event processing
      if (this.eventProcessingInterval) {
        clearInterval(this.eventProcessingInterval);
        this.eventProcessingInterval = null;
      }

      // Only process remaining events if we're still authenticated
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await processEvents();
        }
      } catch (error) {
        logger.warn('Error processing final events:', error);
      }

      // Reset initialization state
      this.isInitialized = false;
      this.eventProcessorService = null;

      logger.info('Application shutdown completed');

    } catch (error) {
      logger.error('Error during application shutdown:', error);
    }
  }

  getInitializationStatus(): boolean {
    return this.isInitialized;
  }

  public async triggerEventProcessing(): Promise<void> {
    try {
      logger.debug('Manually triggering event processing...');
      await processEvents();
    } catch (error) {
      logger.error('Error in manual event processing:', error);
    }
  }

  public async reinitializeSubscriptions(): Promise<void> {
    try {
      logger.info('Reinitializing subscriptions after authentication...');
      
      // Only reinitialize if we're already initialized
      if (!this.isInitialized) {
        logger.debug('App not initialized, skipping subscription reinitialization');
        return;
      }

      // Clean up existing subscription
      if (this.eventProcessingSubscriptionId) {
        // WebSocket unsubscribe removed
        this.eventProcessingSubscriptionId = null;
      }

      // Set up new subscription via WebSocket Manager
      await this.setupEventProcessingSubscription();
      
      logger.info('Subscriptions reinitialized successfully');
    } catch (error) {
      logger.error('Error reinitializing subscriptions:', error);
    }
  }
}

// Export singleton instance
export const appInitialization = AppInitializationService.getInstance();

// Export for manual initialization
export { AppInitializationService };

// Helper functions
export const initializeApp = async (): Promise<void> => {
  await appInitialization.initialize();
};

export const shutdownApp = async (): Promise<void> => {
  await appInitialization.shutdown();
};

export const triggerEventProcessing = async (): Promise<void> => {
  await appInitialization.triggerEventProcessing();
};

export const reinitializeSubscriptions = async (): Promise<void> => {
  await appInitialization.reinitializeSubscriptions();
}; 